import "../styles/archived-people.scss";
import "../styles/people.scss";
import { useSelector } from "react-redux";
import PageLayout from "../components/page-layout";
import Card from "../components/card";
import { SearchBox } from "../components/search-box";
import Grid, { ColumnHeader, GridCell } from "../components/grid";
import { GuiButton } from "../components/global-ui/gui-button";
import {
  GuiCheckbox,
  CheckBoxHeaderButton,
} from "../components/global-ui/gui-checkbox";
import { Modal } from "../components/modal";
import { showToast } from "../components/toast";
import { useUpdateState } from "../utilities/utilities";
import actions from "../actions/actions";
import { USER_TYPE_LABELS, UserTypeBadge } from "../utilities/constants";



function PersonInitials({ name }) {
  const cleanName = (name || "")
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const initials = cleanName
    ? cleanName.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : null;
  return (
    <div className="initials">
      {initials || <i className="fa fa-user" style={{ color: "#ccc", fontSize: "1.2em" }} />}
    </div>
  );
}

export default function ArchivedPeoplePage() {
  let [state, updateState] = useUpdateState({
    list: [],
    loading: false,
    search: "",
    gridReset: 0,
    saving: false,
    showSingleModal: false,
    showBulkModal: false,
    selectedPerson: null,
  });

  let [checkboxState, updateCheckboxState, setCheckboxState] = useUpdateState({});

  async function loadPage(page, pageSize, search) {
    updateState({ loading: true });
    let result = await actions.people.getArchivedPeopleGrid(search, page, pageSize);
    const list = result?.list || [];
    const totalCount = result?.totalCount || 0;
    updateState({ loading: false, list });
    return { list, totalCount };
  }

  function onSearchChange(search) {
    updateState({ search, gridReset: state.gridReset + 1 });
  }

  function toggleAllCheckboxes() {
    const any = Object.keys(checkboxState).find((k) => checkboxState[k]);
    if (any) {
      setCheckboxState({});
    } else {
      setCheckboxState(
        state.list.reduce((acc, row) => {
          acc[row.id] = true;
          return acc;
        }, {})
      );
    }
  }

  async function restoreSingle() {
    updateState({ saving: true });
    const result = await actions.people.restorePeople([state.selectedPerson.id]);
    updateState({ saving: false, showSingleModal: false, selectedPerson: null });
    if (result?.success) {
      showToast("success", "User has been restored");
      setCheckboxState({});
      updateState({ gridReset: state.gridReset + 1 });
    } else {
      showToast("error", "Unable to restore user. Please try again.");
    }
  }

  async function restoreBulk() {
    const ids = Object.keys(checkboxState)
      .filter((k) => checkboxState[k])
      .map(Number);
    updateState({ saving: true });
    const result = await actions.people.restorePeople(ids);
    updateState({ saving: false, showBulkModal: false });
    if (result?.success) {
      showToast("success", "Users have been restored");
      setCheckboxState({});
      updateState({ gridReset: state.gridReset + 1 });
    } else {
      showToast("error", "Unable to restore selected users. Please try again.");
    }
  }

  const selectedCount = Object.keys(checkboxState).filter((k) => checkboxState[k]).length;
  const bulkDisabled = selectedCount === 0;

  function formatDate(dt) {
    if (!dt) return "";
    return new Date(dt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  return (
    <PageLayout title="Archived People" className="archived-people">
      <Card>
        <div className="archived-toolbar">
          <SearchBox
            onSearchChange={onSearchChange}
            searching={state.loading}
            minLength={3}
            dataQa="archived-search"
          />
          <div className="archived-selection-bar">
            <span className={"archived-selected-count" + (selectedCount > 0 ? " has-selection" : "")}>
              {selectedCount} selected
            </span>
            <GuiButton
              className={"btn-outline btn-restore-bulk" + (bulkDisabled ? "" : " active")}
              disabled={bulkDisabled}
              onClick={() => !bulkDisabled && updateState({ showBulkModal: true })}
              dataQa="bulk-restore-btn"
            >
              <i className="fas fa-undo-alt" /> Restore
            </GuiButton>
          </div>
        </div>

        <Grid
          header={
            <div className="header-row row">
              <ColumnHeader className="check">
                <CheckBoxHeaderButton
                  checked={Object.keys(checkboxState).find((k) => checkboxState[k])}
                  onClick={toggleAllCheckboxes}
                />
              </ColumnHeader>
              <ColumnHeader className="name">Name</ColumnHeader>
              <ColumnHeader className="usertype">User Type</ColumnHeader>
              <ColumnHeader className="username">Username</ColumnHeader>
              <ColumnHeader className="deletedat">Deleted At</ColumnHeader>
              <ColumnHeader className="deletedby">Deleted By</ColumnHeader>
              <ColumnHeader className="actions" />
            </div>
          }
          itemTemplate={(p) => (
            <div className="row" key={p.id}>
              <GridCell className="check">
                <GuiCheckbox
                  name={"" + p.id}
                  value={checkboxState[p.id]}
                  onChange={updateCheckboxState}
                />
              </GridCell>
              <GridCell className="name">
                <PersonInitials name={p.name} />
                <span className="person-name">{p.name}</span>
              </GridCell>
              <GridCell className="usertype">
                <UserTypeBadge userTypeId={p.userTypeId} />
              </GridCell>
              <GridCell className="username">{p.userName}</GridCell>
              <GridCell className="deletedat">{formatDate(p.deletedAt)}</GridCell>
              <GridCell className="deletedby">{p.deletedBy || ""}</GridCell>
              <GridCell className="actions">
                <GuiButton
                  className="btn-outline btn-sm"
                  dataQa={"restore-" + p.id}
                  onClick={() => updateState({ showSingleModal: true, selectedPerson: p })}
                >
                  Restore
                </GuiButton>
              </GridCell>
            </div>
          )}
          reset={state.gridReset}
          onItemsNeeded={(page, pageSize) => loadPage(page, pageSize, state.search)}
          onPageChanged={() => setCheckboxState({})}
        />
      </Card>

      {state.showSingleModal && (
        <Modal
          className="archived-restore-modal"
          showModal={state.showSingleModal}
          onClose={() => updateState({ showSingleModal: false, selectedPerson: null })}
        >
          <div className="archived-modal-header">
            <span>Confirm Restore User</span>
            <i
              className="fa fa-close close-primary-icon cursor-pointer"
              onClick={() => updateState({ showSingleModal: false, selectedPerson: null })}
            />
          </div>
          <div className="modal-footer-hr-divider" />
          <div className="archived-modal-content">
            <p>
              Are you sure you want to restore{" "}
              <strong>{state.selectedPerson?.name}</strong> to the system?
            </p>
          </div>
          <div className="archived-modal-footer">
            <GuiButton
              className="btn-outline"
              onClick={() => updateState({ showSingleModal: false, selectedPerson: null })}
            >
              Cancel
            </GuiButton>
            <GuiButton onClick={restoreSingle} loading={state.saving} dataQa="confirm-restore-btn">
              Restore User
            </GuiButton>
          </div>
        </Modal>
      )}

      {state.showBulkModal && (
        <Modal
          className="archived-restore-modal"
          showModal={state.showBulkModal}
          onClose={() => updateState({ showBulkModal: false })}
        >
          <div className="archived-modal-header">
            <span>Confirm Restore Users</span>
            <i
              className="fa fa-close close-primary-icon cursor-pointer"
              onClick={() => updateState({ showBulkModal: false })}
            />
          </div>
          <div className="modal-footer-hr-divider" />
          <div className="archived-modal-content">
            <p>Are you sure you want to restore these users to the system?</p>
          </div>
          <div className="archived-modal-footer">
            <GuiButton
              className="btn-outline"
              onClick={() => updateState({ showBulkModal: false })}
            >
              Cancel
            </GuiButton>
            <GuiButton onClick={restoreBulk} loading={state.saving} dataQa="confirm-bulk-restore-btn">
              Restore Users
            </GuiButton>
          </div>
        </Modal>
      )}
    </PageLayout>
  );
}